// src/pages/promos/EditPromoPage.tsx
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import PlaneLoadingScreen from "@/components/shared/ui/PageLoadingScreen";
import PromoForm from "@/components/promos/forms/PromoForm";
import { usePromoDetail, useUpdatePromoMutation } from "@/api/features/promos";
import type { CreatePromoPayload, UpdatePromoPayload } from "@/api/features/promos";

export default function EditPromoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const promoId = Number(id);
  const { data: promo, isLoading } = usePromoDetail(Number.isFinite(promoId) ? promoId : null);
  const updateMut = useUpdatePromoMutation(promoId);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (payload: CreatePromoPayload | UpdatePromoPayload) => {
    setError(null);
    updateMut.mutate(payload as UpdatePromoPayload, {
      onSuccess: () => navigate("/promos"),
      onError: (e) => setError(e.message || "Failed to update promo"),
    });
  };

  return (
    <AdminLayout>
      <Breadcrumbs />
      {isLoading || !promo ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <PlaneLoadingScreen fullscreen={false} label="Loading promo…" />
        </div>
      ) : (
        <>
          <div className="mb-5">
            <h1 className="text-lg font-semibold text-gray-900">Edit — {promo.name}</h1>
            <p className="text-sm text-gray-500">Update this discount campaign.</p>
          </div>
          <div className="w-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <PromoForm
              mode="edit"
              initial={promo}
              submitting={updateMut.isPending}
              error={error}
              onCancel={() => navigate("/promos")}
              onSubmit={onSubmit}
            />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
